import { type FC, type ReactElement } from 'react';
import { Navigate, useParams } from 'react-router';
import { useIsDlMobile } from '../../hooks/useDlMobile';
import MobileView from '../Mobile';

type MobileGuardProps = {
    children: ReactElement;
} & (
    | {
          // Show the <MobileView> notice; user-facing dead-end with a "Back
          // to home page" link.
          mode: 'notice';
      }
    | {
          // Redirect to a viewable equivalent. Substitutions:
          //   :projectUuid -> useParams().projectUuid (if present)
          mode: 'redirect';
          to: string;
      }
);

/**
 * v1.5 PRD §7 — wraps a route element. On desktop it's a no-op (renders
 * children). On wrapper-signaled mobile, it either shows the MobileView
 * notice or navigates to a sensible target. The redirect target supports
 * `:projectUuid` substitution from the current route's params so we can
 * use the same component for every project-scoped route in Routes.tsx.
 */
const MobileGuard: FC<MobileGuardProps> = (props) => {
    const isMobile = useIsDlMobile();
    const params = useParams<{
        projectUuid?: string;
        savedQueryUuid?: string;
    }>();

    if (!isMobile) {
        return props.children;
    }

    if (props.mode === 'notice') {
        return <MobileView projectUuid={params.projectUuid} />;
    }

    // Redirect mode — substitute :projectUuid and :savedQueryUuid into the
    // target path. We only support these two params because they cover
    // every redirect target the PRD specifies.
    let target = props.to;
    if (params.projectUuid) {
        target = target.replace(':projectUuid', params.projectUuid);
    }
    if (params.savedQueryUuid) {
        target = target.replace(':savedQueryUuid', params.savedQueryUuid);
    }
    return <Navigate replace to={target} />;
};

export default MobileGuard;
